"use client";

import FormResetPassword from "@/components/blocks/Form/FormResetPassword";
import { Button } from "@/components/ui/button";
import CodeInput from "@/components/ui/code-input";
import Input, { InputProps } from "@/components/ui/input";
import Loading from "@/components/ui/loading";
import RouterLink from "@/components/ui/router-link";
import { ValidateFormItem } from "@/components/ui/validate";
import useCountDown from "@/hooks/useCountDown";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { useScopedI18n } from "@/messages/client";
import {
  getImageCaptcha,
  postEmailCode,
  postImageCaptcha,
  postPhoneCode,
  postResetPassword,
} from "@/server/user";
import { useDebounceFn, useThrottleFn } from "ahooks";
import { ChevronRight, CircleHelp, Mail, Smartphone } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Controller, FormProvider, FormState, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { match } from "ts-pattern";
import { v4 as uuidv4 } from "uuid";
import FormPhoneAreaCode from "../Form/FormPhoneAreaCode";
import { useCodeMessage } from "@/hooks/useCodeMessage";
import { cn } from "@/lib/utils";
import useLocaleRouter from "@/hooks/useLocaleRouter";
import ResetPasswordSuccessSVG from "./reset-passwd-success.svg";

// 传入手机号或者邮箱，中间部分使用*号替换
const hideMiddle = (account: string) => {
  const len = account.length;
  const start = account.slice(0, 3);
  const end = account.slice(-3);
  return `${start}${"*".repeat(len - 6)}${end}`;
};

function ImageCodeInput(
  props: InputProps & {
    identifier?: string;
    setIdentifier?: (identifier: string) => void;
  }
) {
  const t = useScopedI18n("找回密码");

  const { data, isMutating, trigger } = useSWRMutation(
    () =>
      props.identifier
        ? `/customers/captcha?identifier=${props.identifier}`
        : null,
    async (_, { arg }: { arg: { identifier: string } }) => {
      const res = await getImageCaptcha({ identifier: arg.identifier });

      return res;
    }
  );

  const { run } = useDebounceFn(
    () => {
      props.setIdentifier?.(uuidv4());
    },
    {
      wait: 300,
    }
  );

  useEffect(() => {
    if (props.identifier) {
      trigger({ identifier: props.identifier });
    }
  }, [props.identifier, trigger]);

  return (
    <div className="mt-5 flex items-center space-x-5 md:space-x-8 lg:space-x-12">
      <Input
        {...props}
        className="w-full flex-1"
        placeholder={t("请输入右侧图形验证码")}
      />

      <Loading loading={isMutating} hideText>
        <div
          className="relative h-7 w-20 flex-shrink-0 cursor-pointer bg-slate-200 md:h-10 md:w-[7.5rem]"
          onClick={run}>
          {data?.image && (
            <Image src={data.image} alt="Code" fill className="object-cover" />
          )}
        </div>
      </Loading>
    </div>
  );
}

function CodeEmailInput(props: InputProps & { email: string }) {
  const t = useScopedI18n("注册模块");

  const locale = useCurrentLocale();

  const { count, start, isLoading, clear } = useCountDown(60);

  const { isMutating, trigger } = useSWRMutation(
    props.email ? `/api/email/code/${props.email}` : null,
    () => {
      if (!props.email) return;

      return postEmailCode({
        email: props.email,
        language: locale === "cn" ? "zh" : locale,
      });
    },
    {
      onSuccess: (res) => {
        if (res && "data" in res && res.data === "success") {
          start();

          toast.success(t("验证码已发送至邮箱"));
        } else {
          if (res && "errors" in res) {
            toast.error(res.errors[0].message);
          }
        }
      },
      onError: () => {
        clear();
        toast.error(t("验证码发送失败"));
      },
    }
  );

  return (
    <Input
      {...props}
      placeholder={t("请输入验证码")}
      suffix={
        <Button
          variant="link"
          type="button"
          loading={isMutating}
          disabled={isLoading}
          className="cursor-pointer whitespace-nowrap pl-2 pr-0 text-xs text-[#2967E3] transition-opacity hover:opacity-80"
          onClick={() => {
            trigger();
          }}>
          {isLoading
            ? t<any, any>("重新发送", {
                count: count ?? 0,
              })
            : t("获取验证码")}
        </Button>
      }
    />
  );
}

function CodeSMSInput(
  props: InputProps & { phoneNumber: string; phoneArea: string }
) {
  const t = useScopedI18n("登录模块");

  const locale = useCurrentLocale();

  const { count, start, isLoading, clear } = useCountDown(60);

  const { isMutating, trigger } = useSWRMutation(
    props.phoneNumber ? `/api/sms/code/${props.phoneNumber}` : null,
    () => {
      if (!props.phoneNumber) return;

      return postPhoneCode({
        phoneNumber: props.phoneNumber,
        phoneArea: props.phoneArea ?? "86",
        language: locale === "cn" ? "zh" : locale,
      });
    },
    {
      onSuccess: (res) => {
        if (res && "data" in res && res?.data === "success") {
          start();

          toast.success(t("手机验证码发送成功"));
        } else {
          if (res && "errors" in res) {
            toast(res.errors?.[0]?.message ?? "发送失败");
          }
        }
      },
      onError: () => {
        clear();
        toast.error(t("手机验证码发送失败"));
      },
    }
  );

  return (
    <Input
      {...props}
      placeholder={t("请输入验证码")}
      autoComplete="off"
      suffix={
        <Button
          variant="link"
          type="button"
          loading={isMutating}
          disabled={isLoading}
          className="cursor-pointer whitespace-nowrap pl-2 pr-0 text-xs text-[#2967E3] transition-opacity hover:opacity-80"
          onClick={() => {
            trigger();
          }}>
          {isLoading
            ? t<any, any>("重新发送", {
                count: count ?? 0,
              })
            : t("获取验证码")}
        </Button>
      }
    />
  );
}

function Step1(props: {
  onCallback: (params: {
    account: string;
    type: "email" | "phone";
    phoneArea?: string;
    captcha: string;
  }) => void;
  identifier?: string;
  setIdentifier?: (identifier: string) => void;
}) {
  const t = useScopedI18n("找回密码");

  const methods = useForm<{
    email?: string;
    phoneNumber?: string;
    phoneArea?: string;
    captcha: string;
  }>();

  const locale = useCurrentLocale();

  const router = useLocaleRouter();

  const [verify, setVerify] = useState<"email" | "phone">("email");

  const { control, handleSubmit } = methods;

  const { trigger, isMutating } = useSWRMutation(
    "/customers/captcha",
    (
      _: string,
      {
        arg,
      }: {
        arg: {
          identifier: string;
          captcha: string;
        };
      }
    ) => {
      return postImageCaptcha({
        identifier: arg.identifier,
        captcha: arg.captcha,
      });
    }
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!props.identifier) return;

      // 过滤空字符串
      Object.keys(data).forEach((key) => {
        if (!data[key as keyof typeof data]) {
          delete data[key as keyof typeof data];
        }
      });

      const res = await trigger({
        identifier: props.identifier,
        captcha: data.captcha,
      });

      if (!res.success) {
        toast.error(t("图形验证码错误"));

        return;
      }

      props.onCallback({
        account: (data.email || data.phoneNumber) ?? "",
        type: verify,
        phoneArea: data.phoneArea,
        captcha: data.captcha,
      });
    } catch (error) {
      console.log(error);
    }
  });

  return (
    <div className="flex flex-col justify-center">
      <div className="flex items-center justify-center pt-8 md:justify-between md:pt-0">
        <div className="flex flex-col items-center md:flex-row md:space-x-2">
          <Image src="/icons/unlock.svg" alt="Unlock" width={40} height={40} />
          <h1
            className={cn(
              "mt-2.5 text-2xl font-medium text-[#FF5100] md:mt-0 en:text-base",
              {
                "text-xl": locale === "en",
              }
            )}>
            {t("找回POWERQUARK账号")}
          </h1>
        </div>

        <div className="hidden items-center enabled:text-xs text-sm font-medium text-[#383838] md:flex  whitespace-nowrap">
          {t<any, any>("登录链接", {
            link: (
              <RouterLink
                href="/login"
                className="flex items-center text-[#4876FF]  whitespace-nowrap">
                <span>{t("去登录")}</span>
                <ChevronRight size={16} />
              </RouterLink>
            ),
          })}
        </div>
      </div>

      <p className="py-5 text-sm md:text-xs lg:py-9 lg:text-sm">
        {t("忘记密码描述")}
      </p>

      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          {match(verify)
            .with("email", () => (
              <Controller
                control={control}
                name="email"
                rules={{
                  required: {
                    value: true,
                    message: t("请输入您的myPQ 账户电子邮箱地址"),
                  },
                }}
                render={({ field }) => (
                  <ValidateFormItem name="email">
                    <Input
                      {...field}
                      placeholder={t(
                        "请输入您的myPQ 账户电子邮箱地址"
                      )}
                    />
                  </ValidateFormItem>
                )}
              />
            ))
            .with("phone", () => (
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("请输入您的myPQ 账户电子邮箱地址"),
                  },
                  pattern: {
                    value: /^[1-9]\d{1,14}$/,
                    message: t("请输入有效的手机号"),
                  },
                }}
                render={({ field }) => (
                  <ValidateFormItem name="phoneNumber">
                    <Input
                      {...field}
                      type="tel"
                      autoComplete="off"
                      placeholder={t(
                        "请输入您的myPQ 账户电子邮箱地址"
                      )}
                      prefixs={
                        <Controller
                          name="phoneArea"
                          control={control}
                          render={({ field }) => (
                            <FormPhoneAreaCode
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      }
                    />
                  </ValidateFormItem>
                )}
              />
            ))
            .otherwise(() => null)}

          <Controller
            control={control}
            name="captcha"
            rules={{
              required: {
                value: true,
                message: t("请输入右侧图形验证码"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem name="captcha">
                <ImageCodeInput
                  identifier={props.identifier}
                  value={field.value}
                  onChange={field.onChange}
                  setIdentifier={props.setIdentifier}
                />
              </ValidateFormItem>
            )}
          />

          <div className="mt-6 flex justify-end md:hidden">
            <div className="flex items-center text-sm font-medium text-[#383838]">
              {t<any, any>("登录链接", {
                link: (
                  <RouterLink
                    href="/login"
                    className="flex items-center text-[#4876FF]">
                    <span>{t("去登录")}</span>
                    <ChevronRight size={16} />
                  </RouterLink>
                ),
              })}
            </div>
          </div>

          <Button
            className="hidden w-full rounded md:mt-6 md:block lg:mt-11"
            loading={isMutating}
            type="submit">
            {t("下一步")}
          </Button>

          <div className="mt-16 grid grid-cols-2 gap-4 md:hidden">
            <Button
              type="button"
              className="rounded-full text-[#FA5850]"
              variant="secondary"
              onClick={() => {
                router.replace("/login");
              }}>
              {t("取消")}
            </Button>

            <Button type="submit" className="rounded-full">
              {t("下一步")}
            </Button>
          </div>

          <div className="mt-10 text-xs md:mt-4 lg:text-sm">
            {t<any, any>("协议描述", {
              privacy: (
                <RouterLink
                  href="/privacy-policy"
                  className="inline-block text-[#FF5100]">
                  {t("隐私政策")}
                </RouterLink>
              ),
            })}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

function Step2(props: {
  account: {
    account: string;
    type: "email" | "phone";
    phoneArea?: string;
    captcha: string;
  };
  identifier?: string;
  setStep: (step: number) => void;
}) {
  const t = useScopedI18n("找回密码");

  const methods = useForm();

  const { control, handleSubmit, getValues } = methods;

  const codeMessage = useCodeMessage();

  const { trigger, isMutating } = useSWRMutation(
    "/customers/resetPassword",
    (
      _: string,
      {
        arg,
      }: {
        arg: {
          code: string;
          captcha: string;
          password: string;
          email?: string;
          phoneNumber?: string;
          phoneArea?: string;
          identifier: string;
        };
      }
    ) => {
      return postResetPassword(arg);
    }
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!props.identifier) return;

      const res = await trigger({
        code: data.code,
        captcha: props.account.captcha,
        identifier: props.identifier,
        password: data.password,
        email:
          props.account.type === "email" ? props.account.account : undefined,
        phoneNumber:
          props.account.type === "phone" ? props.account.account : undefined,
        phoneArea:
          props.account.type === "phone" ? props.account.phoneArea : undefined,
      });

      if ("errors" in res) {
        toast.error(
          codeMessage(res.errors[0].extensions.code, res.errors[0].message)
        );

        return;
      }

      props.setStep(3);
    } catch (error) {}
  });

  return (
    <div className="flex flex-col justify-center">
      <div className="flex flex-col items-center pt-8 md:flex-row md:space-x-2 md:pt-0">
        <Image
          src="/icons/reset-password.svg"
          alt="Reset Password"
          width={40}
          height={40}
        />
        <h1 className="mt-2 text-2xl font-medium text-[#FF5100] md:mt-0">
          {t("密码重置")}
        </h1>
      </div>

      <div className="mt-4 flex items-center space-x-2 text-sm text-[#2C2C2C]">
        <span>{props.account.type === "email" ? t("邮箱") : t("手机号")}:</span>
        <span>{props.account.account}</span>
      </div>

      <FormProvider {...methods}>
        <form
          className="mt-6 space-y-5 lg:mt-11 lg:space-y-6"
          onSubmit={onSubmit}>
          {props.account?.type === "email" ? (
            <Controller
              control={control}
              name="code"
              rules={{
                required: {
                  value: true,
                  message: t("请输入验证码"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="code">
                  <CodeEmailInput
                    value={field.value}
                    onChange={field.onChange}
                    email={props.account.account}
                  />
                </ValidateFormItem>
              )}
            />
          ) : (
            <Controller
              control={control}
              name="code"
              rules={{
                required: {
                  value: true,
                  message: t("请输入验证码"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="code">
                  <CodeSMSInput
                    value={field.value}
                    onChange={field.onChange}
                    phoneNumber={props.account.account}
                    phoneArea={props.account.phoneArea ?? "86"}
                  />
                </ValidateFormItem>
              )}
            />
          )}

          <Controller
            control={control}
            name="password"
            rules={{
              required: {
                value: true,
                message: t("请输入新密码"),
              },
              pattern: {
                // 密码长度8-60个字符，密码需包含数字、字母和特殊字符，密码中不能含有空格
                value:
                  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,60}$/,
                message: t("密码格式限制"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem name="password">
                <FormResetPassword
                  placeholder={t("新密码")}
                  value={field.value}
                  onChange={field.onChange}
                />
              </ValidateFormItem>
            )}
          />

          <Controller
            control={control}
            name="confirmPassword"
            rules={{
              required: {
                value: true,
                message: t("请输入确认密码"),
              },
              validate: (value) => {
                if (value !== getValues("password")) {
                  return t("两次密码不一致");
                }
              },
            }}
            render={({ field }) => (
              <ValidateFormItem name="confirmPassword">
                <Input {...field} type="password" placeholder={t("确认密码")} />
              </ValidateFormItem>
            )}
          />

          <div className="flex items-center space-x-4 pb-5 pt-20 md:space-x-7 md:pb-0 md:pt-0">
            <Button
              variant="outline"
              className="flex-1 rounded-full border-[#A6A6A6] text-[#383838] md:rounded"
              type="button"
              onClick={() => props.setStep(1)}>
              {t("上一步")}
            </Button>
            <Button
              className="flex-1 rounded-full md:rounded"
              type="submit"
              loading={isMutating}>
              {t("提交")}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

function Step3(props: {
  account: {
    account: string;
    type: "email" | "phone";
    phoneArea?: string;
  };
}) {
  const t = useScopedI18n("找回密码");

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="pt-14 md:pt-0">
        <Image
          src="/look.webp"
          className="hidden md:block"
          alt="Success"
          width={120}
          height={120}
        />

        <Image
          src={ResetPasswordSuccessSVG}
          className="md:hidden"
          alt="Success"
          width={240}
          height={150}
        />
      </div>

      <p className="mt-5 text-lg font-medium md:mt-6 lg:mt-12">
        {t("密码重置成功")}
      </p>

      <p className="mt-3 text-lg text-[#383838]">
        {t<any, any>("您的账号{account}", {
          account: hideMiddle(props.account.account),
        })}
      </p>

      <p className="mt-3 text-xs text-[#808080]">{t("请牢记您的新密码")}</p>

      <RouterLink
        href="/login"
        className="mb-5 mt-20 flex h-11 w-full items-center justify-center rounded-full bg-[#E67263] text-sm text-white transition-colors duration-300 hover:bg-[#FF5100] hover:text-white md:mb-0 md:mt-6 md:h-8 md:w-60 md:rounded lg:mt-8">
        {t("返回登录页面")}
      </RouterLink>
    </div>
  );
}

export default function ResetPassword() {
  const t = useScopedI18n("找回密码");

  const [step, setStep] = useState(1);

  const [identifier, setIdentifier] = useState<string>();

  const [account, setAccount] = useState<{
    account: string;
    type: "email" | "phone";
    phoneArea?: string;
    captcha: string;
  }>({
    account: "",
    type: "email",
    phoneArea: "86",
    captcha: "",
  });

  const onStep1Callback = (params: {
    account: string;
    type: "email" | "phone";
    phoneArea?: string;
    captcha: string;
  }) => {
    setAccount(params);

    setStep(2);
  };

  useEffect(() => {
    setIdentifier(uuidv4());
  }, []);

  return (
    <div className="flex min-h-[calc(100vh-2.75rem)] flex-col md:min-h-[calc(100vh-7.125rem)]">
      <div className="login_container flex-1">
        <div className="content_container">
          <div className="flex bg-white md:my-7 md:rounded-2xl md:shadow-[0px_4px_8px_rgba(0,0,0,0.1)]">
            <div className="hidden w-[45%] flex-shrink-0 md:block">
              <Image
                src="/reset-password-bg.webp"
                alt="POWERQUARK"
                width={548}
                height={600}
              />
            </div>
            <div className="flex flex-1 flex-col justify-between md:pb-4 md:pl-6 md:pr-6 md:pt-5 lg:pb-8 lg:pl-10 lg:pr-14 lg:pt-10 xl:pb-14 xl:pl-16 xl:pr-20 xl:pt-14">
              {match(step)
                .with(1, () => (
                  <Step1
                    onCallback={onStep1Callback}
                    identifier={identifier}
                    setIdentifier={setIdentifier}
                  />
                ))
                .with(2, () => (
                  <Step2
                    account={account}
                    identifier={identifier}
                    setStep={setStep}
                  />
                ))
                .with(3, () => <Step3 account={account} />)
                .otherwise(() => null)}

              <div className="mt-5 flex items-center justify-center md:mt-0">
                <div className="flex items-center space-x-2 text-xs text-[#4371FE] md:text-sm md:text-black">
                  <CircleHelp size={18} />

                  <RouterLink href="/account-help" target="_blank">
                    {t("点击获取更多帮助？")}
                  </RouterLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
